import type * as React from "react";

import {
  Button,
  ButtonGroup,
  type ButtonProps,
} from "../../components/ui/button";
import {
  Icon,
  type IconName,
  type IconProps,
} from "../../components/ui/icon";
import { Menu, MenuPopup, MenuTrigger } from "../../components/ui/menu";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useContentBreakpoint } from "../../hooks/use-content-size";
import type { Omittable } from "../../lib/props";

/** Icon-only counterpart for each labeled button size. */
const ICON_ONLY_SIZE: Partial<
  Record<NonNullable<ButtonProps["size"]>, ButtonProps["size"]>
> = {
  default: "icon",
  lg: "icon-lg",
  sm: "icon-sm",
  xl: "icon-xl",
  xs: "icon-xs",
};

export type ToolbarActionProps = Omit<ButtonProps, "children"> & Omittable<{
  /** Optional leading icon shown beside the label at full width. Omit it
   *  (and `collapsedIcon`) for an action that stays text-only at compact
   *  widths. */
  icon?: IconName;
  /** Icon the action collapses to at compact widths. Defaults to `icon`;
   *  given on its own it lets a text-only action ("Add item") still fold
   *  to a bare glyph ("+") when the toolbar runs out of room. */
  collapsedIcon?: IconName;
  label: string;
  iconSize?: IconProps["size"];
  /** Hover tooltip on the action. */
  tooltip?: string;
  /** Alternate actions for this control — renders it as a split button whose
   *  trailing chevron segment opens them in a menu. The primary press stays
   *  `onClick`, so the default action is one click either way. */
  menuItems?: React.ReactNode;
  /** Accessible name for the chevron segment. @default "More options" */
  menuLabel?: string;
}>;

export function ToolbarAction({
  icon,
  collapsedIcon,
  label,
  iconSize,
  tooltip,
  menuItems,
  menuLabel = "More options",
  size = "xs",
  variant,
  disabled,
  ...buttonProps
}: ToolbarActionProps): React.ReactElement {
  const { isAtOrBelow } = useContentBreakpoint();
  const isCollapsed = isAtOrBelow("md");
  const iconOnlySize = ICON_ONLY_SIZE[size ?? "default"] ?? size;
  const collapsedGlyph = collapsedIcon ?? icon;
  const collapseToIcon = isCollapsed && collapsedGlyph != null;

  const button = (
    <Button
      disabled={disabled}
      size={collapseToIcon ? iconOnlySize : size}
      variant={variant}
      // The bare glyph has no text, so the label names it; a caller's own
      // `aria-label` still wins.
      aria-label={collapseToIcon ? label : undefined}
      {...buttonProps}
    >
      {collapseToIcon ? (
        <Icon name={collapsedGlyph} size={iconSize} />
      ) : icon ? (
        <Icon name={icon} size={iconSize} />
      ) : null}
      {!collapseToIcon && (
        <Text size={null} truncate>
          {label}
        </Text>
      )}
    </Button>
  );

  const withTooltip = tooltip ? (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipPopup side="top">{tooltip}</TooltipPopup>
    </Tooltip>
  ) : (
    button
  );

  if (!menuItems) return withTooltip;

  // The chevron is its own segment, so it carries the primary's visual props
  // but none of its behavior — the group squares the seam between them and
  // the pair reads as one control.
  return (
    <Menu>
      <ButtonGroup>
        {withTooltip}
        <MenuTrigger
          render={
            <Button
              aria-label={menuLabel}
              disabled={disabled}
              size={iconOnlySize}
              variant={variant}
            />
          }
        >
          <Icon name="chevron-down" />
        </MenuTrigger>
      </ButtonGroup>
      <MenuPopup align="end">{menuItems}</MenuPopup>
    </Menu>
  );
}
